import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { playSfx } from '../../utils/sounds';
import './training-fx.scss';

const COLORS = ['#6b298a', '#97b000', '#fac334', '#f07c3e', '#e5483d', '#c9e84f', '#a666cc', '#3e99db'];

const reducedMotion = () => Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

export const useToasts = () => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach((timer) => clearTimeout(timer)), []);

  const push = useCallback((text, kind = 'gold') => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev.slice(-2), { id, text, kind }]);
    timers.current.push(setTimeout(() => setToasts((prev) => prev.filter((toast) => toast.id !== id)), 2900));
  }, []);

  return [toasts, push];
};

export const announceAchievements = (items, push) => {
  (items || []).forEach((item, index) => {
    setTimeout(() => {
      push(`${item.icon} Нове досягнення: ${item.title}`, 'gold');
      playSfx('up');
    }, 700 + index * 900);
  });
};

export const TrainingToasts = ({ toasts }) => (
  <div className="tr-toasts" aria-live="polite">
    {toasts.map((toast) => (
      <div key={toast.id} className={`tr-toast tr-toast--${toast.kind}`}>{toast.text}</div>
    ))}
  </div>
);

TrainingToasts.propTypes = {
  toasts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    kind: PropTypes.string,
  })).isRequired,
};

export const Confetti = ({ burst = null }) => {
  const canvasRef = useRef(null);
  const parts = useRef([]);
  const frame = useRef(0);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!burst || !canvas || reducedMotion()) return;
    const count = (navigator.hardwareConcurrency || 8) <= 4 ? Math.round(burst.count / 2) : burst.count;
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const originX = rect.width / 2;
    const originY = rect.height * 0.3;

    for (let i = 0; i < count; i += 1) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.1;
      const speed = 6 + Math.random() * 9;
      parts.current.push({
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        g: 0.28 + Math.random() * 0.1,
        w: 6 + Math.random() * 6,
        h: 4 + Math.random() * 6,
        rot: Math.random() * 6,
        vr: (Math.random() - 0.5) * 0.4,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        life: 0,
        round: Math.random() < 0.3,
      });
    }

    const ctx = canvas.getContext('2d');
    const loop = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);
      const list = parts.current;
      for (let i = list.length - 1; i >= 0; i -= 1) {
        const p = list[i];
        p.vx *= 0.985;
        p.vy = p.vy * 0.985 + p.g;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.life += 1;
        if (p.y > rect.height + 20 || p.life > 320) {
          list.splice(i, 1);
        } else {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.fillStyle = p.color;
          if (p.round) {
            ctx.beginPath();
            ctx.arc(0, 0, p.w / 2.4, 0, 6.3);
            ctx.fill();
          } else {
            ctx.scale(1, Math.cos(p.life * 0.15));
            ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          }
          ctx.restore();
        }
      }
      if (list.length) {
        frame.current = requestAnimationFrame(loop);
      } else {
        ctx.clearRect(0, 0, rect.width, rect.height);
      }
    };
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(loop);
  }, [burst]);

  return <canvas ref={canvasRef} className="tr-confetti" aria-hidden="true" />;
};

Confetti.propTypes = {
  burst: PropTypes.shape({
    id: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
  }),
};
